import { GEMATIK_ROOM } from '../constants';

/**
 * Get room display name with Gematik-specific event support.
 *
 * Checks for custom Gematik room name state event (de.gematik.tim.room.name)
 * before falling back to standard Matrix room name (m.room.name).
 *
 * This is required for Gematik TI-Messenger compliance where custom state
 * events carry authoritative room metadata.
 *
 * @param {Room} room - Matrix Room object from matrix-js-sdk
 * @param {string} [fallback='Unnamed Room'] - Default value if no name found
 * @returns {string} Room display name or fallback
 *
 * @example
 * const displayName = getRoomDisplayName(room);
 * // Returns: "Team Discussion" (from Gematik event if present)
 * //       or "Team Discussion" (from Matrix event if Gematik not present)
 * //       or "Unnamed Room" (if neither present)
 */
export const getRoomDisplayName = (room, fallback = 'Unnamed Room') => {
  // Defensive check for room initialization phase
  if (!room?.currentState) {
    return fallback;
  }

  // First check for Gematik custom name event
  const gematikNameEvent = room.currentState.getStateEvents(GEMATIK_ROOM.ROOM_NAME, '');
  if (gematikNameEvent) {
    const content = gematikNameEvent.getContent();
    const name = content?.name;
    // Explicit type check and non-empty validation
    if (typeof name === 'string' && name.trim()) {
      return name;
    }
  }

  // Fallback to standard Matrix room name
  return room.name || fallback;
};

/**
 * Get room topic with Gematik-specific event support.
 *
 * Checks for custom Gematik room topic state event (de.gematik.tim.room.topic)
 * before falling back to standard Matrix room topic (m.room.topic).
 *
 * This is required for Gematik TI-Messenger compliance where custom state
 * events carry authoritative room metadata.
 *
 * @param {Room} room - Matrix Room object from matrix-js-sdk
 * @param {string} [fallback=''] - Default value if no topic found
 * @returns {string} Room topic or fallback
 *
 * @example
 * const topic = getRoomTopic(room);
 * // Returns: "Project updates and discussions" (from Gematik event if present)
 * //       or "Project updates" (from Matrix event if Gematik not present)
 * //       or "" (if neither present)
 */
export const getRoomTopic = (room, fallback = '') => {
  // Defensive check for room initialization phase
  if (!room?.currentState) {
    return fallback;
  }

  // First check for Gematik custom topic event
  const gematikTopicEvent = room.currentState.getStateEvents(GEMATIK_ROOM.ROOM_TOPIC, '');
  if (gematikTopicEvent) {
    const topic = gematikTopicEvent.getContent()?.topic;
    // Explicit type check and non-empty validation (consistent with getRoomDisplayName)
    if (typeof topic === 'string' && topic.trim()) {
      return topic;
    }
  }

  // Fallback to standard Matrix room topic
  const standardTopicEvent = room.currentState.getStateEvents('m.room.topic', '');
  if (standardTopicEvent) {
    const topic = standardTopicEvent.getContent()?.topic;
    if (typeof topic === 'string' && topic.trim()) {
      return topic;
    }
  }

  return fallback;
};